import { ILoadingAnimationTimeoutMessage } from 'library/ts/ui/components/loading/interfaces/ILoadingAnimationTimeoutMessage';
import { ILoadingAnimationTimeoutMessageConfig } from 'library/ts/ui/components/loading/interfaces/ILoadingAnimationTimeoutMessageConfig';
import { CSSValue } from 'library/ts/ui/enums/css/CSSValue';
import { Utilities } from 'library/ts/utilities/Utilities';

/**
 * @copyright © Paramount Streaming
 * @author Daniel Silva
 * @description Loading Animation Timeout Message component class
 */
export class LoadingAnimationTimeoutMessage implements ILoadingAnimationTimeoutMessage {
	#enabled: boolean;
	#config: ILoadingAnimationTimeoutMessageConfig;
	#utils: Utilities;
	#container: HTMLElement; // container for timeout message
	#loadingAnimationContainer: HTMLElement; // parent container where this component is appended
	#bufferTimeout: number;
	#bufferDismissed: boolean = true;
	#bufferDuration: number;

	/**
	 * @description Constructor
	 * @param loadingAnimationContainer {HTMLElement} parent container where this component is appended
	 * @param config {ILoadingAnimationBackgroundImageConfig} config object
	 */
	constructor( loadingAnimationContainer: HTMLElement, config: ILoadingAnimationTimeoutMessageConfig ) {
		this.#loadingAnimationContainer = loadingAnimationContainer;
		this.#config = config;
		this.#utils = window.PARA.PlayerServices.Utilities;
		this.#enabled = config?.enabled;
		const defaultTimeoutForMessage = 5000;
		this.#bufferDuration = Number( config?.timeoutForMessage || defaultTimeoutForMessage );
		this.#createTimeoutMessageContainer();
	}

	get name(): string {
		return 'LoadingAnimationTimeoutMessage';
	}

	/**
	 * @description Enables the timeout message
	 */
	enable = (): void => {
		this.#enabled = true;
	};

	/**
	 * @description Disables and hides the timeout message
	 */
	disable = (): void => {
		this.#enabled = false;
		this.hide();
	};

	/**
	 * @description Shows the timeout message after the specified timeout
	 */
	show = (): void => {
		if (
			!this.#enabled
			|| !( this.#utils.Type.isElement( this.#container ) )
			|| !this.#bufferDismissed ) {
			return;
		}
		clearTimeout( this.#bufferTimeout );
		this.#bufferTimeout = window.setTimeout( this.#showTimeoutMessage, this.#bufferDuration );
		this.#bufferDismissed = false;
	};

	/**
	 * @description Hides the timeout message
	 */
	hide = (): void => {
		if ( this.#utils.Type.isElement( this.#container ) ) {
			this.#hideTimeoutMessage();
		}
	};

	/**
	 * @description Internal method to display the timeout message.
	 * @private
	 */
	#showTimeoutMessage = (): void => {
		clearTimeout( this.#bufferTimeout );
		if ( this.#utils.Type.isElement( this.#container ) ) {
			this.#container.style.setProperty( 'opacity', '1', CSSValue.IMPORTANT );
		}
	};

	/**
	 * @description Internal method to hide the timeout message.
	 * @private
	 */
	#hideTimeoutMessage = (): void => {
		this.#bufferDismissed = true;
		clearTimeout( this.#bufferTimeout );
		if ( this.#utils.Type.isElement( this.#container ) ) {
			this.#container.style.setProperty( 'opacity', '0', CSSValue.IMPORTANT );
		}
	};

	/**
	 * @description Creates the timeout message container.
	 * @private
	 */
	#createTimeoutMessageContainer = (): void => {
		if ( this.#config?.timeoutMessage ) {
			this.#container = this.#utils.DOM.stringToElement( `
				<div
					style="	
						transform: translateY(90px);
						position: absolute;
						transition: 0.5s ease opacity;
						opacity: 0;
						color: #eee;
						font-family: var(--font-family-regular), Verdana, sans-serif !important;
						z-index: 2;" 
					class="loading-animation-optimizing-container">
					${this.#config.timeoutMessage}
				</div>
			` );
			this.#loadingAnimationContainer?.appendChild( this.#container );
		}
	};
}